/**
 * useBriefing — react-query state for the guided onboarding wizard.
 *
 * Loads the project (auto-creating it) + the briefing, debounced autosave,
 * and the completion mutation. On completion it invalidates the onboarding
 * state so the dashboard reflects the auto-satisfied steps.
 */
import { useCallback, useEffect, useRef } from "react";
import {
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import * as api from "./briefingService";

const AUTOSAVE_DELAY_MS = 800;

export function usePracticeAreas() {
  return useQuery({
    queryKey: ["briefing", "practice-areas"],
    queryFn: api.fetchPracticeAreas,
    staleTime: Infinity,
  });
}

export function useQualificationQuestions(areaIds = []) {
  const sorted = [...areaIds].sort();
  return useQuery({
    queryKey: ["briefing", "qualification-questions", sorted.join(",")],
    queryFn: () => api.fetchQualificationQuestions(sorted),
    enabled: sorted.length > 0,
    staleTime: Infinity,
  });
}

export function useAccountUsers() {
  return useQuery({
    queryKey: ["account", "users"],
    queryFn: api.fetchAccountUsers,
    staleTime: 5 * 60 * 1000,
  });
}

export function useDepartments() {
  return useQuery({
    queryKey: ["account", "departments"],
    queryFn: api.fetchDepartments,
    staleTime: 5 * 60 * 1000,
  });
}

export function useServiceMenuOptions(provider) {
  return useQuery({
    queryKey: ["briefing", "service-menu-options", provider || "none"],
    queryFn: () => api.fetchServiceMenuOptions(provider || "none"),
    enabled: !!provider && provider !== "none",
    staleTime: Infinity,
  });
}

export function useBriefing(clientId) {
  const qc = useQueryClient();
  const queryKey = ["briefing", clientId];

  const query = useQuery({
    queryKey,
    enabled: !!clientId,
    queryFn: async () => {
      const project = await api.ensureProject(clientId);
      const briefing = await api.fetchBriefing(clientId);
      return { project, briefing };
    },
  });

  const setBriefing = useCallback(
    (updated) => {
      // Merge, don't replace: PUT /briefing and /briefing/complete return only
      // the ImplementationBriefing row (sanitizeBriefingForClient) — fields the
      // GET route derives and adds on top (e.g. `inheritedJuridical`, embed
      // onboarding's step 1 lock) would otherwise vanish from state after the
      // very first autosave.
      qc.setQueryData(queryKey, (prev) =>
        prev ? { ...prev, briefing: { ...prev.briefing, ...updated } } : prev,
      );
    },
    [qc, clientId], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const saveMutation = useMutation({
    mutationFn: (patch) => api.saveBriefing(clientId, patch),
    onSuccess: setBriefing,
  });

  // Debounced autosave that coalesces rapid edits into one PUT.
  const timerRef = useRef(null);
  const pendingRef = useRef({});
  const saveMutationRef = useRef(saveMutation);
  saveMutationRef.current = saveMutation;

  const autosave = useCallback((patch) => {
    pendingRef.current = { ...pendingRef.current, ...patch };
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      const toSend = pendingRef.current;
      pendingRef.current = {};
      saveMutationRef.current.mutate(toSend);
    }, AUTOSAVE_DELAY_MS);
  }, []);

  // Flush any pending autosave immediately (e.g. before completing).
  const flush = useCallback(async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const toSend = pendingRef.current;
    pendingRef.current = {};
    if (Object.keys(toSend).length > 0) {
      const updated = await api.saveBriefing(clientId, toSend);
      setBriefing(updated);
    }
  }, [clientId, setBriefing]);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  const completeMutation = useMutation({
    mutationFn: async () => {
      await flush();
      const projectId = query.data?.project?.id;
      return api.completeBriefing(projectId);
    },
    // NOTE: intentionally does NOT write the completed briefing into the shared
    // cache here. Doing so would flip ImplementationEntry to the dashboard and
    // unmount the wizard before the peak-end success screen shows. The wizard
    // holds the completed briefing locally and calls setBriefing() only when the
    // user dismisses the success screen ("Ir para o painel").
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["onboarding", clientId] });
    },
  });

  return {
    project: query.data?.project,
    briefing: query.data?.briefing,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    autosave,
    flush,
    saveNow: saveMutation.mutateAsync,
    isSaving: saveMutation.isPending,
    setBriefing,
    complete: completeMutation.mutateAsync,
    isCompleting: completeMutation.isPending,
    completeError: completeMutation.error,
  };
}
